import React, { lazy, Suspense, useContext, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Col,
  Dropdown,
  Form,
  Layout,
  Menu,
  notification,
  Row,
  Typography,
} from "antd";
import UserSettings from "./pages/UserSettings";
import TrackedCardsModal from "./modals/TrackedCardsModal";
import "./modals/TrackedCardsModal.css";
import {
  BookOutlined,
  ContainerOutlined,
  DesktopOutlined,
  ExperimentOutlined,
  LoginOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PieChartOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BrowserRouter as Router, Link, Route, Routes } from "react-router-dom";
// Lazy load page components for better client-side performance
import { useAuth0 } from "@auth0/auth0-react";
import LoginButton from "./auth/LoginButton";
import LogoutButton from "./auth/LogoutButton";
import PageLoader from "./common/PageLoader";
import Logo from "./brand/Logo";
import settings from "./settings";
import "./App.css";
import { ThemeContext } from "./context/ThemeContext";
import DarkModeToggle from "./components/DarkModeToggle";
// Page components (lazy loaded)
const Home = lazy(() => import("./pages/Home"));
const ExpansionsDashboard = lazy(() => import("./pages/ExpansionsDashboard"));
const CardAnalysis = lazy(() => import("./pages/CardAnalysis"));
const SellerAnalysis = lazy(() => import("./pages/SellerAnalysis"));
const Collections = lazy(() => import("./pages/Collections"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const DeckBuilder = lazy(() => import("./pages/DeckBuilder"));
const DeckSimulation = lazy(() => import("./pages/DeckSimulation"));
const MetaNews = lazy(() => import("./pages/MetaNews"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ShopLanding = lazy(() => import("./pages/ShopLanding"));
const ChartMockPage = lazy(() => import("./pages/ChartMockPage"));
const ChartMixedMockPage = lazy(() => import("./pages/ChartMixedMockPage"));
const ChartLanguageMixedMockPage = lazy(
  () => import("./pages/ChartLanguageMixedMockPage"),
);
const SearchFiltersMockPage = lazy(
  () => import("./pages/SearchFiltersMockPage"),
);

const { Header, Content, Footer, Sider } = Layout;
const { Title, Paragraph } = Typography;

const menuItems = (isAuthenticated) =>
  [
    {
      key: "home",
      icon: <DesktopOutlined />,
      label: <Link to="/">Home 🏡</Link>,
    },
    isAuthenticated && {
      key: "card-analysis",
      icon: <PieChartOutlined />,
      label: <Link to="/card-analysis">Card Analysis 👀 (beta)</Link>,
    },
    isAuthenticated && {
      key: "deck-builder",
      icon: <ContainerOutlined />,
      label: <Link to="/deck-builder">Deck Builder 🃏</Link>,
    },
    isAuthenticated && {
      key: "deck-simulation",
      icon: <ExperimentOutlined />,
      label: <Link to="/deck-simulation">Deck Simulation 🧪</Link>,
    },
    isAuthenticated && {
      key: "meta-news",
      icon: <MailOutlined />,
      label: <Link to="/meta-news">Meta News</Link>,
    },
    {
      key: "shop-landing",
      icon: <ContainerOutlined />,
      label: <Link to="/shop">B2B Shop</Link>,
    },
    {
      key: "contact-us",
      icon: <MailOutlined />,
      label: <Link to="/contact-us">Contact Us</Link>,
    },
    {
      key: "privacy-policy",
      icon: <BookOutlined />,
      label: <Link to="/privacy-policy">Privacy Policy</Link>,
    },
    !isAuthenticated && {
      key: "login",
      icon: <LoginOutlined />,
      label: <LoginButton />,
    },
    isAuthenticated && {
      key: "logout",
      icon: <LogoutOutlined />,
      label: <LogoutButton />,
    },
  ].filter(Boolean);

function App() {
  const {
    isAuthenticated,
    error,
    isLoading,
    user,
    getAccessTokenSilently,
    logout,
  } = useAuth0();
  const [collapsed, setCollapsed] = useState(false);
  const [trackedCards, setTrackedCards] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loadingTrackedCards, setLoadingTrackedCards] = useState(false);
  const [isUserSettingsModalVisible, setIsUserSettingsModalVisible] =
    useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [form] = Form.useForm();
  const [isTrackedCardsModalVisible, setIsTrackedCardsModalVisible] =
    useState(false);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const showTrackedCardsModal = async () => {
    setIsTrackedCardsModalVisible(true);
    setLoadingTrackedCards(true);
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://valuedraw.com/api/v1",
          scope: "read:current_user",
        },
      });
      const response = await fetch(
        "https://api-auth.valuedraw.com/cards/tracked-cards/",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      const data = await response.json();
      setTrackedCards(data);
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: `An error occurred while fetching tracked cards: ${err}`,
      });
      setTrackedCards([]);
    } finally {
      setLoadingTrackedCards(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingCard(null);
    form.resetFields();
  };

  const showUserSettingsModal = () => {
    setIsUserSettingsModalVisible(true);
  };

  const handleUserSettingsModalCancel = () => {
    setIsUserSettingsModalVisible(false);
  };

  const handleTrackedCardsCancel = () => {
    setIsTrackedCardsModalVisible(false);
  };

  const userProfileDropdown =
    isAuthenticated && user ? (
      <Menu>
        <Menu.Item key="user-settings" onClick={showUserSettingsModal}>
          User Settings
        </Menu.Item>
        <Menu.Item key="tracked-cards" onClick={showTrackedCardsModal}>
          Tracked Cards
        </Menu.Item>
        <Menu.Item
          key="logout"
          onClick={() =>
            logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            })
          }
        >
          Logout
        </Menu.Item>
      </Menu>
    ) : null;

  const handleEditCard = (card) => {
    setEditingCard(card);
    form.setFieldsValue({
      id: card.id,
      name: card.name,
      initial_price: card.initial_price,
      target_price: card.target_price,
    });
  };

  const handleDeleteTrackedCard = async (cardId) => {
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://valuedraw.com/api/v1",
          scope: "read:current_user",
        },
      });
      const response = await fetch(
        `https://api-auth.valuedraw.com/cards/tracked-cards/${cardId}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (response.ok) {
        notification.success({
          message: "Success",
          description: "Card has been successfully deleted.",
        });
        setTrackedCards(trackedCards.filter((card) => card.id !== cardId));
      } else {
        const errorData = await response.json();
        notification.error({
          message: "Error",
          description: `Failed to delete card: ${errorData.message || "Unknown error"}`,
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "An error occurred while deleting the card.",
      });
    }
  };

  const handleUpdateCard = async (values) => {
    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://valuedraw.com/api/v1",
          scope: "read:current_user",
        },
      });
      const response = await fetch(
        `https://api-auth.valuedraw.com/cards/tracked-cards/${values.id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: values.name,
            initial_price: values.initial_price,
            target_price: values.target_price,
            operator: values.operator,
          }),
        },
      );

      if (response.ok) {
        notification.success({
          message: "Success",
          description: "Card has been successfully updated.",
        });
        setTrackedCards(
          trackedCards.map((card) =>
            card.id === values.id
              ? {
                  ...card,
                  name: values.name,
                  initial_price: values.initial_price,
                  target_price: values.target_price,
                }
              : card,
          ),
        );
      } else {
        const errorData = await response.json();
        notification.error({
          message: "Error",
          description: `Failed to update card: ${errorData.message || "Unknown error"}`,
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "An error occurred while updating the card.",
      });
    } finally {
      setEditingCard(null);
      form.resetFields();
    }
  };

  if (error) {
    return (
      <Row>
        <Alert
          message="An error occurred.."
          description={error.toString()}
          type="error"
        />
        <br />
        <a href="/">Go back</a>
      </Row>
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  const ThemeApp = () => {
    const { darkMode } = useContext(ThemeContext);
    return (
      <Router>
        <div className={darkMode ? "dark-mode" : "light-mode"}>
          <Layout style={{ minHeight: "100vh" }}>
            {collapsed ? (
              <Sider
                collapsible
                theme={darkMode ? "dark" : "light"}
                onCollapse={toggleCollapsed}
              >
                <Logo showImageOnly={true} />
                {isAuthenticated ? <div /> : <div />}
                <Menu
                  theme={darkMode ? "dark" : "light"}
                  mode="inline"
                  defaultSelectedKeys={["home"]}
                  items={menuItems(isAuthenticated)}
                />
              </Sider>
            ) : null}
            <Layout>
              <Header
                style={{
                  backgroundColor: "var(--header-background)",
                  color: "var(--text-color)",
                  padding: 0,
                }}
              >
                <Row
                  align="middle"
                  justify="space-between"
                  className="app-header-row"
                >
                  <Col xs={5} md={2}>
                    <Button
                      type="primary"
                      onClick={toggleCollapsed}
                      style={{ marginLeft: 12 }}
                    >
                      {collapsed ? (
                        <MenuUnfoldOutlined />
                      ) : (
                        <MenuFoldOutlined />
                      )}
                    </Button>
                  </Col>
                  <Col xs={14} md={20}>
                    <Logo
                      showImageOnly={false}
                      className="app-logo-text"
                      style={{
                        padding: 0,
                        margin: 0,
                        textAlign: "center",
                        color: darkMode
                          ? "var(--text-color)"
                          : settings.MAIN_COLOR,
                        fontSize: "36px",
                      }}
                    />
                  </Col>
                  <Col xs={5} md={2} className="header-actions">
                    <DarkModeToggle />
                    {isAuthenticated && user && (
                      <Dropdown
                        overlay={userProfileDropdown}
                        trigger={["click"]}
                      >
                        <a
                          onClick={(e) => e.preventDefault()}
                          style={{ marginRight: 20 }}
                        >
                          <Avatar
                            size="large"
                            src={user.picture}
                            icon={<UserOutlined />}
                          />
                        </a>
                      </Dropdown>
                    )}
                  </Col>
                </Row>
              </Header>
              <Content style={{ margin: "16px" }}>
                <Suspense fallback={<PageLoader tip="Loading..." />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route
                      path="/dashboard"
                      element={<ExpansionsDashboard />}
                    />
                    <Route path="/meta-news" element={<MetaNews />} />
                    <Route path="/collections" element={<Collections />} />
                    <Route path="/card-analysis" element={<CardAnalysis />} />
                    <Route
                      path="/seller-analysis"
                      element={<SellerAnalysis />}
                    />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/deck-builder" element={<DeckBuilder />} />
                    <Route
                      path="/deck-simulation"
                      element={<DeckSimulation />}
                    />
                    <Route path="/shop" element={<ShopLanding />} />
                    {typeof window !== "undefined" && window.Cypress && (
                      <>
                        <Route
                          path="/search-filters-mock"
                          element={<SearchFiltersMockPage />}
                        />
                        <Route path="/chart-mock" element={<ChartMockPage />} />
                        <Route
                          path="/chart-mixed-mock"
                          element={<ChartMixedMockPage />}
                        />
                        <Route
                          path="/chart-language-mixed-mock"
                          element={<ChartLanguageMixedMockPage />}
                        />
                      </>
                    )}
                  </Routes>
                </Suspense>
              </Content>
              <Footer style={{ textAlign: "center" }}>
                ValueDraw Market Watch ©2025 - By Kio🚀
              </Footer>
            </Layout>
          </Layout>

          {/* New Tracked Cards Modal Component */}
          <TrackedCardsModal
            visible={isTrackedCardsModalVisible}
            onCancel={handleTrackedCardsCancel}
            trackedCards={trackedCards}
            onDeleteCard={handleDeleteTrackedCard}
            onUpdateCard={handleUpdateCard}
            loading={loadingTrackedCards}
          />

          <UserSettings
            visible={isUserSettingsModalVisible}
            onCancel={handleUserSettingsModalCancel}
          />
        </div>
      </Router>
    );
  };

  return <ThemeApp />;
}

export default App;
